import {
  type PropsWithChildren,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { tzOffset } from '@date-fns/tz';
import { type RouteProps } from 'react-router';
import { useTernaryDarkMode } from 'usehooks-ts';
import {
  addOrUpdateUserPreference,
  deleteUserPreference,
  fetchUserPreferenceSwallowingAllErrors,
  fetchUserPreferenceThrowingServiceUnavailable,
} from '@common/api/rest';
import {
  fetchGuidedTourConfiguration,
  fetchGuidedTourContent,
  fetchUnderMaintenanceText,
} from '@common/api/webResources';
import '@common/brand/applyBrandFavicons';
import { brandConfig, isTecalor } from '@common/brand/brandConfig';
import { BrandProvider } from '@common/brand/BrandContext';
import Bluebox from '@common/components/Bluebox';
import Error from '@common/components/Error';
import useFeedbackPopup from '@common/components/FeedbackPopUp/useFeedbackPopup';
import GuidedTourPopup from '@common/components/GuidedTourPopup';
import GuidedTourPopupLoading from '@common/components/GuidedTourPopup/GuidedTourPopupLoading';
import Loading from '@common/components/Loading';
import PageFooter from '@common/components/PageFooter';
import PageFooterLoading from '@common/components/PageFooter/PageFooterLoading';
import PageHeader from '@common/components/PageHeader';
import ApplicationSwitcher from '@common/components/PageHeader/ApplicationSwitcher';
import LanguageSwitcher from '@common/components/PageHeader/LanguageSwitcher';
import PageHeaderMenu from '@common/components/PageHeader/PageHeaderMenu';
import SimpleAuthenticatedDesktopMenu from '@common/components/PageHeader/PageHeaderMenu/SimpleAuthenticatedDesktopMenu';
import useSupportPopup from '@common/components/SupportPopUp/useSupportPopup';
import ToastsContainer from '@common/components/Toasts/Container';
import { BLUEBOX_MSG_URL, GUIDED_TOUR_BASE_URL } from '@common/constants/blobStorage';
import ApplicationContext from '@common/containers/ApplicationContext';
import TimezoneInconsistencyPopup from '@common/containers/TimezoneInconsistencyPopup';
import usePageLayout from '@common/hooks/pageLayout/usePageLayout';
import useAccessRights from '@common/hooks/useAccessRights';
import useDemoStatus from '@common/hooks/useDemoStatus';
import useEulaRequirement from '@common/hooks/useEulaRequirement';
import useUIElementState from '@common/hooks/useUIElementState';
import i18n from '@common/i18n/config';
import { useAppDispatch, useAppSelector } from '@common/store';
import {
  getContractTypes,
  getCurrentUser,
  getDeviceTypes,
  getLanguages,
  getTimeZones,
  logout,
} from '@common/store/actions/async';
import { useFeatureFlag } from '@common/store/reducers/featureFlags';
import { type IanaId } from '@common/types/brandedTypes';
import { type GuidedTour, type GuidedTourRef } from '@common/types/guidedTour';
import { UserState } from '@common/types/store';
import analytics, { usePageViewTracker } from '@common/utils/analytics';
import { CurrentBrowserIanaId } from '@common/utils/datetime/timezones';
import { generalErrorCode } from '@common/utils/device/mobileAppConstants';
import sessionStorageManager from '@common/utils/storage/sessionStorageManager';
import {
  LOCAL_STORAGE_UI_THEME,
  SESSION_CHECK_USER_TIMEZONE_KEY,
  USER_PREFERENCE_DONT_SHOW_AGAIN_FOR_TZ_KEY,
  USER_PREFERENCE_LAST_COMPLETED_GUIDED_TOUR_KEY,
} from '@common/utils/storage/storageKeys';
import classnames from '@common/utils/ui/classnames';

const emptyLanguages = {};

type GuidedTourOpener = 'userLogin' | 'footerLink';

function Application({ children }: PropsWithChildren<RouteProps>) {
  const mobileUserMode = useAppSelector((state) => state.main.mobileUserMode);
  const shouldQueryUserData = useAppSelector(
    ({ main: { userState, user } }) => (
      (userState === UserState.LoggedIn && !user)
      || (userState === UserState.Unknown && !mobileUserMode)
    ),
  );
  const { currentUser: user, canAccessUserManagement, isEndUser } = useAccessRights();
  // The pill cluster (ServiceWelt / User management / Toolbox) needs the opposite-app
  // URL for the cross-app links; per-pill role gating happens inside ApplicationSwitcher.
  const hasOppositeApp = !!process.env.OPPOSITE_APP_URL;
  const languages = useAppSelector((state) => {
    if (APP_TYPE === 'um') {
      return emptyLanguages;
    }
    return state.main.languages;
  });
  const deviceRegistrationType = useAppSelector((state) => state.main.deviceRegistrationType);
  const supportContactsInsteadOfFeedback = useFeatureFlag('SupportContactsInsteadOfFeedback');
  const betaAccess = useFeatureFlag('BetaAccess');
  const showBetaBadge = useFeatureFlag('ShowBetaBadge');
  const mustAcceptEula = useEulaRequirement();
  const isDemoMode = useDemoStatus();
  const dispatch = useAppDispatch();
  const [translationsMissing, setTranslationsMissing] = useState(false);
  const [blueboxText, setBlueboxText] = useState<string | undefined>();
  const { isWindowedLayout, setPageLayout } = usePageLayout();
  const [showGuidedTour, setShowGuidedTour] = useState<GuidedTourOpener>();
  const [guidedTourConfig, setGuidedTourConfig] = useState<GuidedTourRef>();
  const [guidedTour, setGuidedTour] = useState<GuidedTour>();
  const {
    shouldShowElement: showTZPopup,
    showElement: openTZPopup,
    hideElement: closeTZPopup,
  } = useUIElementState();
  const {
    isDarkMode: rawIsDarkMode,
    ternaryDarkMode,
    setTernaryDarkMode,
  } = useTernaryDarkMode({
    defaultValue: 'light',
    localStorageKey: `SW.${LOCAL_STORAGE_UI_THEME}`,
  });
  const isDarkMode = isTecalor ? false : rawIsDarkMode;

  usePageViewTracker();

  useEffect(() => {
    const initBluebox = async () => {
      if (BLUEBOX_MSG_URL) {
        setBlueboxText(await fetchUnderMaintenanceText(BLUEBOX_MSG_URL));
      }
    };
    initBluebox();
  }, [setBlueboxText]);

  useEffect(() => {
    dispatch(getTimeZones());
    dispatch(getLanguages());
  }, [dispatch]);

  useEffect(
    () => {
      if (shouldQueryUserData) {
        dispatch(getCurrentUser());
      }
    },
    [dispatch, shouldQueryUserData],
  );

  useEffect(() => {
    if (APP_TYPE === 'sw') {
      if (user && isEndUser) {
        // Only end users are subject to device type filtering
        dispatch(getDeviceTypes());
      }
      if (user && !isEndUser) {
        dispatch(getContractTypes()); // Results in 401 Unauthorised for enduser
      }
    }
  }, [dispatch, isEndUser, user]);

  useEffect(
    () => {
      analytics.initialize();
      const handleFailedLoading = () => {
        setTranslationsMissing(true);
        setPageLayout('windowed');
        if (mobileUserMode) {
          alert(generalErrorCode);
        }
      };
      i18n.on('failedLoading', handleFailedLoading);
      return () => {
        i18n.off('failedLoading', handleFailedLoading);
      };
    },
    [mobileUserMode, setPageLayout],
  );

  useEffect(() => {
    if (user?.language) {
      i18n.changeLanguage(user.language).catch((err) => console.error(err));
    }
  }, [user?.language]);

  const loadGuidedTourFromConfig = useCallback(async (tourConfig: GuidedTourRef) => {
    if (GUIDED_TOUR_BASE_URL) {
      const tour = await fetchGuidedTourContent(GUIDED_TOUR_BASE_URL, tourConfig);
      if (tour) {
        setGuidedTour(tour);
      }
      return tour;
    }
  }, []);

  useEffect(() => {
    if (APP_TYPE === 'sw') {
      if (user && GUIDED_TOUR_BASE_URL) {
        const fetchGuidedTour = async () => {
          const [lastSeenGuidedTourVersion, tourConfig] = await Promise.all([
            fetchUserPreferenceThrowingServiceUnavailable<string>(
              USER_PREFERENCE_LAST_COMPLETED_GUIDED_TOUR_KEY,
            ),
            fetchGuidedTourConfiguration(GUIDED_TOUR_BASE_URL, user.userLevel),
          ]);
          if (tourConfig) {
            setGuidedTourConfig(tourConfig);
          }
          if (
            tourConfig
            && (
              lastSeenGuidedTourVersion == null
              || parseInt(lastSeenGuidedTourVersion, 10) < parseInt(tourConfig.version, 10)
            )
          ) {
            const tour = await loadGuidedTourFromConfig(tourConfig);
            if (tour) {
              setShowGuidedTour('userLogin');
            }
          }
        };
        void fetchGuidedTour()
          .catch(() => {
            // If Error 503 is received, just catch it
          });
      }
    }
  }, [dispatch, isEndUser, loadGuidedTourFromConfig, user]);

  const closeGuidedTour = useCallback(() => {
    if (showGuidedTour) {
      if (guidedTour && showGuidedTour === 'userLogin') {
        addOrUpdateUserPreference(
          USER_PREFERENCE_LAST_COMPLETED_GUIDED_TOUR_KEY,
          guidedTour.version,
        );
      }
      setShowGuidedTour(undefined);
    }
  }, [guidedTour, showGuidedTour]);

  useEffect(() => {
    const warnAboutTZDiffIfAllowed = async (
      ianaId: IanaId,
    ) => {
      // Use offsets because timezone mapping of BE TimeZone API is not specific enough.
      // For example FLE Standard time includes Helsinki, Kyiv, Riga, Sofia, Tallinn, and Vilnius,
      // but it is currently mapped to Europe/Kiev.
      const now = new Date();
      const userTZOffset = tzOffset(ianaId, now);
      const browserTZOffset = tzOffset(CurrentBrowserIanaId, now);
      const dontShowPreference = await fetchUserPreferenceSwallowingAllErrors(
        USER_PREFERENCE_DONT_SHOW_AGAIN_FOR_TZ_KEY,
      );
      if (userTZOffset === browserTZOffset) {
        // User is in configured timezone
        if (dontShowPreference !== undefined) {
          await deleteUserPreference(USER_PREFERENCE_DONT_SHOW_AGAIN_FOR_TZ_KEY);
        }
      } else if (CurrentBrowserIanaId !== dontShowPreference) {
        openTZPopup();
      }
      sessionStorageManager.removeItem(SESSION_CHECK_USER_TIMEZONE_KEY);
    };

    if (
      user?.ianaId && sessionStorageManager.getItem<boolean>(SESSION_CHECK_USER_TIMEZONE_KEY)
    ) {
      warnAboutTZDiffIfAllowed(user.ianaId);
    }
  }, [openTZPopup, user?.ianaId]);

  const dontShowAgain = useCallback(async () => {
    await addOrUpdateUserPreference(
      USER_PREFERENCE_DONT_SHOW_AGAIN_FOR_TZ_KEY,
      CurrentBrowserIanaId,
    );
    closeTZPopup();
  }, [closeTZPopup]);

  const onLogout = useCallback(
    () => {
      setGuidedTour(undefined);
      setShowGuidedTour(undefined);
      setGuidedTourConfig(undefined);
      dispatch(logout());
    },
    [dispatch],
  );

  const openTourPopup = useCallback(async () => {
    if (guidedTourConfig) {
      setShowGuidedTour('footerLink');
      if (!guidedTour) {
        const tour = await loadGuidedTourFromConfig(guidedTourConfig);
        if (!tour) {
          setShowGuidedTour(undefined);
        }
      }
    }
  }, [guidedTour, guidedTourConfig, loadGuidedTourFromConfig]);
  const openTourPopupAction = guidedTourConfig && APP_TYPE === 'sw' ? openTourPopup : undefined;

  const { feedbackPopup, openFeedbackPopup } = useFeedbackPopup();
  const openFeedbackPopupAction = (user?.status === 'active' && !isDemoMode)
    ? openFeedbackPopup
    : undefined;

  const { supportPopup, openSupportPopup } = useSupportPopup();
  const openSupportPopupAction = (user?.status === 'active' && !isDemoMode)
    ? openSupportPopup
    : undefined;

  const timeZoneWarningVisible = user?.ianaId && showTZPopup;

  useLayoutEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('theme-dark');
    } else {
      document.body.classList.remove('theme-dark');
    }
    if (isTecalor) {
      document.body.classList.add('brand-tecalor');
    }
  }, [isDarkMode]);

  useEffect(() => {
    document.title = brandConfig.title;
  }, []);

  return (
    <div
      className={classnames({
        'page-container': true,
        'page-container--windowed': isWindowedLayout,
      })}
    >
      <BrandProvider>
        <ApplicationContext
          isDarkMode={isDarkMode}
          ternaryDarkMode={ternaryDarkMode}
          setTernaryDarkMode={setTernaryDarkMode}
        >
          <Suspense
            fallback={(
              <>
                <PageHeader
                  logoDisabled={!!deviceRegistrationType}
                  showBetaBadge={showBetaBadge}
                  leftAction={(
                    <ApplicationSwitcher
                      isDemoMode={isDemoMode}
                      canAccessUserManagement={canAccessUserManagement}
                      hasOppositeApp={hasOppositeApp}
                      isDeviceRegistration={!!deviceRegistrationType}
                    />
                  )}
                />
                <main className="page-content page-content--windowed">
                  <Loading />
                </main>
                <PageFooterLoading />
              </>
            )}
          >
            {blueboxText && mobileUserMode !== 'headless' && (
              <Bluebox
                text={blueboxText}
                onClose={() => setBlueboxText(undefined)}
              />
            )}
            {mobileUserMode !== 'headless'
              && (
                <PageHeader
                  logoDisabled={!!deviceRegistrationType}
                  showBetaBadge={showBetaBadge}
                  leftAction={(
                    <ApplicationSwitcher
                      isDemoMode={isDemoMode}
                      canAccessUserManagement={canAccessUserManagement}
                      hasOppositeApp={hasOppositeApp}
                      isDeviceRegistration={!!deviceRegistrationType}
                    />
                  )}
                  rightAction={(
                    <>
                      {/* Registration is a standalone flow: only the language switcher, even when logged in. */}
                      {user === undefined || deviceRegistrationType
                        ? <LanguageSwitcher languages={languages} align="right" />
                        : (
                          <>
                            {APP_TYPE === 'sw' && (
                              <PageHeaderMenu
                                user={user}
                                mustAcceptEula={mustAcceptEula}
                                onLogout={onLogout}
                                openFeedbackPopup={openFeedbackPopupAction}
                                showBetaFeatures={betaAccess}
                              />
                            )}
                            {APP_TYPE === 'um' && (
                              <SimpleAuthenticatedDesktopMenu
                                user={user}
                                onLogout={onLogout}
                              />
                            )}
                          </>
                        )}
                    </>
                  )}
                />
              )}
            <main
              className={classnames({
                'page-content': true,
                'page-content--windowed': isWindowedLayout,
              })}
            >
              {translationsMissing
                ? (
                  <div className="container">
                    <Error code={500} />
                  </div>
                )
                : (
                  <>
                    {timeZoneWarningVisible && !(guidedTour && showGuidedTour) && (
                      <TimezoneInconsistencyPopup
                        userIanaId={user.ianaId}
                        onClose={closeTZPopup}
                        onDontShowAgainForThisTimezone={dontShowAgain}
                      />
                    )}
                    {/* Guided tour should appear on top */}
                    {showGuidedTour && guidedTour && (
                      <GuidedTourPopup steps={guidedTour.steps} onClose={closeGuidedTour} />
                    )}
                    {showGuidedTour && !guidedTour && <GuidedTourPopupLoading />}
                    {feedbackPopup}
                    {supportPopup}
                    {children}
                  </>
                )}
            </main>
            {mobileUserMode !== 'headless' && (
              <PageFooter
                openTour={deviceRegistrationType ? undefined : openTourPopupAction}
                openFeedback={supportContactsInsteadOfFeedback || !!deviceRegistrationType
                  ? undefined
                  : openFeedbackPopupAction}
                openSupport={supportContactsInsteadOfFeedback && !deviceRegistrationType
                  ? openSupportPopupAction
                  : undefined}
                privacyPolicyHidden={!!deviceRegistrationType}
              />
            )}
            <ToastsContainer />
          </Suspense>
        </ApplicationContext>
      </BrandProvider>
    </div>
  );
}

export default Application;
